import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  addTableRow,
  deleteTableRow,
  editTableRow,
  moveTableRow,
  setSelectedRow,
  validateSavedSearch,
} from './../actions/editor.actions';
import {
  SAVED_SEARCH_SORT,
  SAVED_SEARCH_SORT_TABLE_LABEL,
  SORT_COLUMN,
  SORT_ORDER,
  SORT_ORDER_OPTIONS,
} from './../constants/editor.constants';
import {
  getEntityTreeFieldsWithFilter,
  getSavedSearchTableRows,
  getSelectedTableRow,
} from './../selectors/editor.selectors';
import ExpandablePanel from './ExpandablePanel';
import ActionCell from './table/ActionCell';
import ColumnCell from './table/ColumnCell';
import EditableSelectCell from './table/EditableSelectCell';
import { useSavedSearchSelector, useTableLastRowVisibilityEffect } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getColumnsWithTranslations } from '../utils/common.utils';

const useStyles = makeStyles({
  tableContainer: {
    overflowX: 'auto',
    '& [class*="middleAlign"]': {
      height: '60px !important',
    },
  },
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  selected: {
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
  select: {
    margin: 3,
    paddingRight: 3,
  },
  table: {
    width: '100%',
  },
});

const columns = [
  {
    header: 'Column',
    accessor: SORT_COLUMN,
    customCell: ColumnCell,
  },
  {
    header: 'Sort Order',
    accessor: SORT_ORDER,
    customCell: EditableSelectCell('sortOrderOprions'),
    width: 200,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
    width: 55,
  },
];

export const sortTableValidationProps = [
  {
    prop: SORT_COLUMN,
    errorLabel: 'Column',
    required: true,
    unique: true,
    tableName: SAVED_SEARCH_SORT_TABLE_LABEL,
  },
];

const SortTable = ({ sortFullWidth }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const rows = useSavedSearchSelector(
    getSavedSearchTableRows,
    { tablePath: SAVED_SEARCH_SORT },
    equalityFn
  );
  const entityColumns = useSelector(
    (state) => getEntityTreeFieldsWithFilter(state, { onlyRootEntityFields: true }),
    equalityFn
  );
  const selectedTableRowId = useSelector(getSelectedTableRow);

  const tableRef = useRef();

  const tableName = SAVED_SEARCH_SORT;

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const columnsWithTranslations = useMemo(() => {
    return getColumnsWithTranslations({ columns, translateString });
  }, [translateString]);

  const addRow = () => {
    const newRow = {
      rowId: uuidv4(),
      [SORT_COLUMN]: '',
      [SORT_ORDER]: 'ASC',
    };

    dispatch(addTableRow({ tableName, newRow }));
    dispatch(setSelectedRow({ rowId: newRow.rowId }));
  };

  const editRow = (params) => {
    dispatch(editTableRow({ ...params, tableName }));
    params.error && dispatch(validateSavedSearch());
  };

  const deleteRow = (params) => {
    dispatch(deleteTableRow({ ...params, tableName }));
  };

  const moveRow = (params) => {
    dispatch(moveTableRow({ ...params, tableName }));
  };

  const handleSelectRow = ({ rowId }) => {
    dispatch(setSelectedRow({ rowId }));
  };

  return (
    <ExpandablePanel label={translateString(SAVED_SEARCH_SORT_TABLE_LABEL)} addRow={addRow}>
      <Grid
        item
        md={12}
        lg={sortFullWidth ? 12 : 8}
        className={classes.tableContainer}
        ref={tableRef}
      >
        <Table
          columns={columnsWithTranslations}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          classes={{ root: classes.table }}
          rows={rows.map((row, index) => ({
            ...row,
            classes,
            index,
            enableRowMovement: true,
            editRow,
            deleteRow,
            entityColumns,
            translateString,
            moveRow,
            handleSelectRow,
            sortOrderOprions: SORT_ORDER_OPTIONS,
            rowsLength: rows.length,
            className: classNames(selectedTableRowId === row.rowId ? classes.selected : ''),
            isSelected: selectedTableRowId === row.rowId,
          }))}
          hidePagination
        />
      </Grid>
    </ExpandablePanel>
  );
};

export default SortTable;
